import React, { useEffect, useState } from "react";
import { useTheme } from "styled-components/macro";
import { IconButton, Modal } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faMegaphone,
  faSpinner,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Body,
  CancelToastContent,
  CancelToastIcon,
  CloseButton,
  CommentField,
  ErrorMessage,
  FeedbackModalWrapper,
  FeedbackModalWrapperNonBlocking,
  Footer,
  FooterButton,
  Header,
  HeaderTitle,
  Intro,
  IntroSubtitle,
  IntroTitle,
  NonBlockingWrapper,
  OverlayWrapper,
  ScoreButton,
  ScoreLabels,
  ScoreRow,
  SubmittedBody,
  SubmittedButton,
  SubmittedSubtitle,
  SubmittedTitle,
  SubmittedWrapper,
  ToastSnackbar,
} from "./PortalFeedbackModal.styles";

type PortalFeedbackModalVariant = "mobile" | "desktop";

interface PortalFeedbackModalProps {
  isOpen: boolean;
  firstName?: string | null;
  score: number | null;
  comment: string;
  isSubmitting: boolean;
  isSubmitted: boolean;
  error: boolean;
  onScoreChange: (score: number) => void;
  onCommentChange: (value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
  onCancel?: () => void;
  variant?: PortalFeedbackModalVariant;
  desktopRightOffset?: string;
  introQuestion: string;
  desktopToastMessage?: React.ReactNode;
  cancelToastMessage?: React.ReactNode;
}

const AUTO_DISMISS_MS = 4000;
const MAX_COMMENT_LENGTH = 4096;
const SCORE_VALUES = Array.from({ length: 11 }, (_, index) => index);

export const PortalFeedbackModal: React.FC<PortalFeedbackModalProps> = ({
  isOpen,
  firstName,
  score,
  comment,
  isSubmitting,
  isSubmitted,
  error,
  onScoreChange,
  onCommentChange,
  onSubmit,
  onClose,
  onCancel,
  variant = "mobile",
  desktopRightOffset,
  introQuestion,
  desktopToastMessage,
  cancelToastMessage,
}) => {
  const theme = useTheme() as { redMedium: string };
  const displayName = firstName?.trim() || "there";
  const isNonBlocking = variant === "desktop";
  const Wrapper = isNonBlocking ? NonBlockingWrapper : OverlayWrapper;
  const ModalWrapper = isNonBlocking
    ? FeedbackModalWrapperNonBlocking
    : FeedbackModalWrapper;
  const [isCancelToastOpen, setIsCancelToastOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsCancelToastOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isSubmitted && !isNonBlocking && isOpen) {
      const timeout = setTimeout(() => {
        onClose();
      }, AUTO_DISMISS_MS);
      return () => clearTimeout(timeout);
    }
  }, [isSubmitted, isNonBlocking, isOpen, onClose]);

  const handleToastClose = () => {
    if (isCancelToastOpen) {
      setIsCancelToastOpen(false);
    }
    onClose();
  };

  if (isNonBlocking && (isSubmitted || (isCancelToastOpen && !isOpen))) {
    return (
      <ToastSnackbar
        open={isSubmitted ? isOpen : isCancelToastOpen && !isOpen}
        onClose={handleToastClose}
        autoHideDuration={AUTO_DISMISS_MS}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        style={{
          right: desktopRightOffset ?? "24px",
          bottom: "24px",
        }}
        action={
          <IconButton
            size="small"
            onClick={handleToastClose}
            sx={{ color: (theme) => theme.palette.common.white }}
          >
            <FontAwesomeIcon icon={faXmark} style={{ fontSize: "16px" }} />
          </IconButton>
        }
        message={
          isCancelToastOpen ? (
            <CancelToastContent>
              <CancelToastIcon icon={faMegaphone} />
              <span>{cancelToastMessage}</span>
            </CancelToastContent>
          ) : (
            desktopToastMessage || (
              <>
                Thank you for your feedback.
                <br />
                We appreciate your time and perspective.
              </>
            )
          )
        }
      />
    );
  }

  const handleCancel = () => {
    onCancel?.();
    if (isNonBlocking && cancelToastMessage) {
      setIsCancelToastOpen(true);
    }
    onClose();
  };

  const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      style={{
        display: "flex",
        ...(isNonBlocking ? { pointerEvents: "none" } : {}),
      }}
      hideBackdrop={isNonBlocking}
      disableEnforceFocus={isNonBlocking}
      disableAutoFocus={isNonBlocking}
      disableRestoreFocus={isNonBlocking}
      disableScrollLock={isNonBlocking}
    >
      <Wrapper
        $desktopRightOffset={desktopRightOffset}
        onClick={handleWrapperClick}
      >
        {isSubmitted ? (
          <SubmittedWrapper>
            <SubmittedBody>
              <FontAwesomeIcon
                icon={faCircleCheck}
                style={{ fontSize: "36px", color: "#60C66A" }}
              />
              <div>
                <SubmittedTitle>Thank you for your feedback</SubmittedTitle>
                <SubmittedSubtitle>
                  We appreciate your time and perspective.
                </SubmittedSubtitle>
              </div>
            </SubmittedBody>
            <SubmittedButton onClick={onClose} type="button">
              OK
            </SubmittedButton>
          </SubmittedWrapper>
        ) : (
          <ModalWrapper>
            <Header>
              <HeaderTitle>Give us feedback</HeaderTitle>
              <CloseButton $disabled={isSubmitting} onClick={onClose} />
            </Header>
            <Body>
              <Intro>
                <IntroTitle>
                  Hi, {displayName}. {introQuestion}
                </IntroTitle>
                <IntroSubtitle>
                  This will help us keep improving the experience.
                </IntroSubtitle>
              </Intro>
              <ScoreRow>
                {SCORE_VALUES.map((value) => (
                  <ScoreButton
                    key={value}
                    $selected={score === value}
                    onClick={() => onScoreChange(value)}
                    disabled={isSubmitting}
                    type="button"
                  >
                    {value}
                  </ScoreButton>
                ))}
              </ScoreRow>
              <ScoreLabels>
                <span>Not at all likely</span>
                <span>Extremely likely</span>
              </ScoreLabels>
              <CommentField
                multiline
                rows={4}
                inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
                placeholder="What's the most important reason for your score?"
                value={comment}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onCommentChange(event.target.value)
                }
                disabled={isSubmitting}
              />
              {error && (
                <ErrorMessage>
                  Something went wrong. Please try again.
                </ErrorMessage>
              )}
            </Body>
            <Footer>
              <FooterButton
                $variant="secondary"
                disabled={isSubmitting}
                onClick={handleCancel}
                type="button"
              >
                CANCEL
              </FooterButton>
              <FooterButton
                $variant="primary"
                disabled={isSubmitting || score === null}
                onClick={onSubmit}
                type="button"
              >
                {isSubmitting ? (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    style={{ color: theme.redMedium }}
                  />
                ) : (
                  "SUBMIT"
                )}
              </FooterButton>
            </Footer>
          </ModalWrapper>
        )}
      </Wrapper>
    </Modal>
  );
};
